import { useContext, createContext, useCallback, useMemo, useState, Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User } from '../../models/user.model';
import { AccessRequirements, checkAccess } from '../../shared/utils/accessControl';
import { LocalStorageKeys } from '../../enums/localStorageKeys';
import { UserRole } from '../../enums/userEnums';
import Notification from '../../shared/components/Notification';
import { NotificationTypes } from '../../enums/notificationTypes';
import { disconnectSocket } from '../../services/socket.service';

export interface SelectedClient {
  id: number;
  name: string;
}

export interface AuthState {
  authenticated: boolean;
  user?: User | undefined;
  selectedClient?: SelectedClient;
}

type SetAuthState = Dispatch<SetStateAction<AuthState>>;

type AuthContentProps = [AuthState, SetAuthState];

const AuthContent = createContext<AuthContentProps | null>(null);

// Initialize default values from localStorage
const userStored = localStorage.getItem(LocalStorageKeys.USER) ?? null;
const user = userStored ? (JSON.parse(userStored) as User) : new User();
const token = localStorage.getItem(LocalStorageKeys.AUTH_TOKEN);
const selectedClientStored = localStorage.getItem(LocalStorageKeys.SELECTED_CLIENT) ?? null;
const selectedClient = selectedClientStored ? (JSON.parse(selectedClientStored) as SelectedClient) : undefined;

const initialValues: AuthState = {
  authenticated: Boolean(token && user?.id),
  user: user,
  selectedClient,
};

const useAuth = () => {
  const context = useContext<AuthContentProps | null>(AuthContent);
  if (!context) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }
  const [auth, setAuth] = context;
  const queryClient = useQueryClient();

  const role = auth.user?.role?.role;
  // CES Admins must pick a client before using any module besides Client Manager and My Profile.
  const needsClientSelection = role === UserRole.CES_ADMIN && !auth.selectedClient;

  // Keyed on the role string so the identity stays stable for consumers' memo deps.
  const canAccess = useCallback((requirements: AccessRequirements) => checkAccess({ role }, requirements), [role]);

  const setAuthenticated = (user?: User) => {
    setAuth((auth) => ({
      ...auth,
      authenticated: true,
      user,
    }));
  };

  const setSelectedClient = (client: SelectedClient) => {
    localStorage.setItem(LocalStorageKeys.SELECTED_CLIENT, JSON.stringify(client));
    setAuth((auth) => ({
      ...auth,
      selectedClient: client,
    }));
  };

  const logout = () => {
    localStorage.removeItem(LocalStorageKeys.AUTH_TOKEN);
    localStorage.removeItem(LocalStorageKeys.USER);
    localStorage.removeItem(LocalStorageKeys.SELECTED_CLIENT);
    // The socket authenticates with the JWT we just cleared; drop it here so the
    // next getSocket() re-handshakes with the new user's token.
    disconnectSocket();
    // Query keys carry no user id, and this path never reloads the page (unlike
    // the 401 redirect), so without this the next user to log in on this tab
    // reads the previous user's cached data.
    queryClient.clear();
    setAuth({ authenticated: false, user: new User() });
    Notification({
      message: 'Logged out succesfully!',
      description: '',
      type: NotificationTypes.SUCCESS,
    });
  };

  return {
    ...auth,
    role,
    needsClientSelection,
    canAccess,
    setAuthenticated,
    setSelectedClient,
    logout,
  };
};

const AuthProvider = (ownProps: any) => {
  const [auth, setAuth] = useState<AuthState>(initialValues);
  const value = useMemo(() => [auth, setAuth] as AuthContentProps, [auth]);
  return <AuthContent.Provider value={value} {...ownProps} />;
};

export { AuthProvider, useAuth };
